import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { adminApi } from "@/lib/adminAuth";
import { toast } from "sonner";
import { Plus, Trash2, Star } from "lucide-react";

const empty = { name: "", avatar_url: "", title: "", bio: "", specialties: [], price_per_reading: 100, languages: ["tr"], is_online: true, is_active: true, is_verified: false, sort_order: 100, commission_pct: 20 };

export default function AdminFalcilar() {
  const [items, setItems] = useState([]);
  const [consultations, setConsultations] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = () => api.get("/admin/falcilar", adminApi()).then(({ data }) => setItems(data));
  const loadC = () => api.get("/admin/consultations", adminApi()).then(({ data }) => setConsultations(data));
  useEffect(() => { load(); loadC(); }, []);

  const save = async () => {
    try {
      if (creating) await api.post("/admin/falcilar", editing, adminApi());
      else await api.patch(`/admin/falcilar/${editing.id}`, editing, adminApi());
      toast.success("Kaydedildi");
      setEditing(null); setCreating(false); load();
    } catch { toast.error("Hata"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Silinsin mi?")) return;
    await api.delete(`/admin/falcilar/${id}`, adminApi()); load();
  };

  const totalRevenue = consultations.filter(c => c.status !== "cancelled").reduce((s, c) => s + (c.platform_commission || 0), 0);

  return (
    <div className="font-admin-body">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-admin-head text-3xl">Falcı Marketplace</h1>
        <button onClick={() => { setCreating(true); setEditing({ ...empty }); }} data-testid="new-falci-btn" className="admin-btn flex items-center gap-2"><Plus className="w-4 h-4" /> Yeni Falcı</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <div className="admin-card p-4"><div className="text-xs text-white/50">Toplam Falcı</div><div className="font-admin-head text-3xl mt-1">{items.length}</div></div>
        <div className="admin-card p-4"><div className="text-xs text-white/50">Konsültasyon</div><div className="font-admin-head text-3xl mt-1">{consultations.length}</div></div>
        <div className="admin-card p-4"><div className="text-xs text-white/50">Platform Komisyon</div><div className="font-admin-head text-3xl mt-1 text-[#F59E0B]">{totalRevenue}<span className="text-sm">j</span></div></div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-8">
        {items.map((f) => (
          <div key={f.id} className="admin-card p-4 flex gap-3">
            <img src={f.avatar_url || ""} alt="" className="w-16 h-16 rounded-full object-cover bg-white/10" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <div className="font-admin-head text-lg">{f.name}</div>
                {f.is_online && <span className="text-[10px] text-emerald-400">● Online</span>}
                {f.is_verified && <span className="text-[10px] text-[#F59E0B]">✓ Onaylı</span>}
              </div>
              <div className="text-xs text-white/50">{f.title}</div>
              <div className="text-xs mt-1 flex items-center gap-2">
                <span className="flex items-center gap-1 text-[#F59E0B]"><Star className="w-3 h-3 fill-[#F59E0B]" />{f.rating}</span>
                <span className="text-white/50">({f.reviews_count})</span>
                <span className="text-white/70">· {f.price_per_reading}j/yorum</span>
                <span className="text-white/50">· %{f.commission_pct} kom.</span>
              </div>
              <div className="flex gap-1 mt-2">
                <button onClick={() => setEditing({ ...f })} className="admin-btn-ghost text-xs px-2 py-1">Düzenle</button>
                <button onClick={() => remove(f.id)} className="admin-btn-ghost text-xs px-2 py-1 text-red-400"><Trash2 className="w-3 h-3" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <h2 className="font-admin-head text-xl mb-3">Konsültasyonlar</h2>
      <div className="admin-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-white/50 uppercase tracking-wider">
            <tr className="border-b border-[#27272A]">
              <th className="p-3">Zaman</th><th className="p-3">Falcı</th><th className="p-3">Tür</th><th className="p-3">Jeton</th><th className="p-3">Komisyon</th><th className="p-3">Durum</th>
            </tr>
          </thead>
          <tbody>
            {consultations.map((c) => (
              <tr key={c.id} className="border-b border-[#18181B]">
                <td className="p-3 text-xs text-white/60">{new Date(c.created_at).toLocaleString("tr-TR")}</td>
                <td className="p-3 text-xs">{items.find(i => i.id === c.falci_id)?.name || c.falci_id.slice(0,8)}</td>
                <td className="p-3">{c.fortune_type}</td>
                <td className="p-3 tabular-nums">{c.tokens_charged}</td>
                <td className="p-3 tabular-nums text-[#F59E0B]">{c.platform_commission}</td>
                <td className="p-3"><span className={c.status === "completed" ? "text-emerald-400" : c.status === "cancelled" ? "text-red-400" : "text-[#F59E0B]"}>{c.status}</span></td>
              </tr>
            ))}
            {consultations.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-white/50">Henüz konsültasyon yok</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4 overflow-y-auto">
          <div className="admin-card p-6 w-full max-w-xl my-8">
            <h2 className="font-admin-head text-xl mb-4">{creating ? "Yeni Falcı" : "Falcı Düzenle"}</h2>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><label className="text-xs text-white/50">Ad</label><input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="admin-input" /></div>
              <div className="col-span-2"><label className="text-xs text-white/50">Avatar URL</label><input value={editing.avatar_url} onChange={(e) => setEditing({ ...editing, avatar_url: e.target.value })} className="admin-input" /></div>
              <div className="col-span-2"><label className="text-xs text-white/50">Ünvan</label><input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} placeholder="20+ yıl deneyim" className="admin-input" /></div>
              <div className="col-span-2"><label className="text-xs text-white/50">Bio</label><textarea value={editing.bio} onChange={(e) => setEditing({ ...editing, bio: e.target.value })} rows={2} className="admin-input" /></div>
              <div className="col-span-2"><label className="text-xs text-white/50">Uzmanlıklar (virgülle)</label><input value={(editing.specialties || []).join(", ")} onChange={(e) => setEditing({ ...editing, specialties: e.target.value.split(",").map(x => x.trim()).filter(Boolean) })} className="admin-input" placeholder="Kahve, Tarot, Aşk" /></div>
              <div><label className="text-xs text-white/50">Fiyat (jeton)</label><input type="number" value={editing.price_per_reading} onChange={(e) => setEditing({ ...editing, price_per_reading: parseInt(e.target.value) || 0 })} className="admin-input" /></div>
              <div><label className="text-xs text-white/50">Platform Komisyon (%)</label><input type="number" value={editing.commission_pct} onChange={(e) => setEditing({ ...editing, commission_pct: parseInt(e.target.value) || 0 })} className="admin-input" /></div>
              <div className="col-span-2 flex gap-4 flex-wrap">
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!editing.is_online} onChange={(e) => setEditing({ ...editing, is_online: e.target.checked })} className="accent-[#F59E0B]" /> Çevrimiçi</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!editing.is_active} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} className="accent-[#F59E0B]" /> Aktif</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!editing.is_verified} onChange={(e) => setEditing({ ...editing, is_verified: e.target.checked })} className="accent-[#F59E0B]" /> Onaylı</label>
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <button onClick={save} className="admin-btn flex-1">Kaydet</button>
              <button onClick={() => { setEditing(null); setCreating(false); }} className="admin-btn-ghost flex-1">İptal</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
